/**
 * Raw mutation service fns for project comments (target-scoped).
 *
 * Comments attach to a `target` = `{ targetType: 'file' | 'brief'; targetId }`.
 * The matching read services + queryOptions helpers (listComments,
 * listCommentReplies, projectCommentsInfiniteQuery, commentRepliesQuery) live
 * canonically in `entities/project-comment`.
 */
import {
  type CommentResponseDto,
  CommentsService,
  type CreateCommentDto,
  type RequestAttachmentUploadDto,
  type UpdateCommentDto,
  type UpdateCommentStatusDto,
} from '@inference.engine/workspace-api-client/web';
import type { CommentTargetRef } from '@/entities/project-comment';
import { workspaceHttp } from '@/lib/api/http-client';

/**
 * Mints a presigned S3 upload URL for a comment attachment through the workspace-api.
 * The backend records a "claim" tying the resulting assetId to the caller — only
 * those claimed assets can later be passed back as `attachmentAssetIds` on a
 * comment create/update.
 */
export const requestCommentAttachmentUpload = async (
  projectId: string,
  target: CommentTargetRef,
  body: { filename: string; contentType: string },
): Promise<{ url: string; fields: Record<string, string>; assetId: string }> => {
  if (!projectId || !target.targetId) {
    throw new Error('Cannot upload comment attachment without projectId and target id.');
  }

  // `contentType` comes from the browser (`file.type`), an arbitrary MIME string, while the
  // generated DTO types it as the server's strict allowlist enum. The server is the real
  // validator, so cast at the boundary rather than narrowing here.
  const response = await CommentsService.requestAttachmentUpload({
    client: workspaceHttp,
    path: { platformProjectId: projectId },
    query: { targetType: target.targetType, targetId: target.targetId },
    body: {
      filename: body.filename,
      contentType: body.contentType as RequestAttachmentUploadDto['contentType'],
    },
  });

  if (!response.data) {
    throw new Error('No upload URL received from workspace-api');
  }

  return response.data;
};

export const createComment = async (
  projectId: string,
  target: CommentTargetRef,
  body: CreateCommentDto,
): Promise<CommentResponseDto> => {
  const response = await CommentsService.create({
    client: workspaceHttp,
    path: { platformProjectId: projectId },
    query: { targetType: target.targetType, targetId: target.targetId },
    body,
  });

  if (!response.data) {
    throw new Error('Failed to create comment');
  }

  return response.data;
};

export const updateComment = async (
  projectId: string,
  target: CommentTargetRef,
  commentId: string,
  body: UpdateCommentDto,
): Promise<CommentResponseDto> => {
  const response = await CommentsService.update({
    client: workspaceHttp,
    path: { platformProjectId: projectId, commentId },
    query: { targetType: target.targetType, targetId: target.targetId },
    body,
  });

  if (!response.data) {
    throw new Error('Failed to update comment');
  }

  return response.data;
};

export const deleteComment = async (
  projectId: string,
  target: CommentTargetRef,
  commentId: string,
): Promise<void> => {
  await CommentsService.delete({
    client: workspaceHttp,
    path: { platformProjectId: projectId, commentId },
    query: { targetType: target.targetType, targetId: target.targetId },
  });
};

export const updateCommentStatus = async (
  projectId: string,
  target: CommentTargetRef,
  commentId: string,
  body: UpdateCommentStatusDto,
): Promise<CommentResponseDto> => {
  const response = await CommentsService.updateStatus({
    client: workspaceHttp,
    path: { platformProjectId: projectId, commentId },
    query: { targetType: target.targetType, targetId: target.targetId },
    body,
  });

  if (!response.data) {
    throw new Error('Failed to update comment status');
  }

  return response.data;
};
